import { useLocale, useTranslations } from "use-intl";

import { RelativeTime } from "@/components/feed/feed-event-primitives";
import {
  formatCompactCommercePrice,
  formatPriceDeltaPercent,
} from "@/components/marketplace/deal-offers-utils";
import { Price } from "@/components/Price";
import type { ClientNotificationItem } from "@garimpa/api/notifications/toClientNotification";

function dealPriceLine(deal: ClientNotificationItem["subject"]["deal"], locale: string) {
  if (!deal) return null;
  const price = formatCompactCommercePrice(deal.price, deal.currency, locale);
  if (!price) return null;
  const referencePrice = formatCompactCommercePrice(deal.referencePrice, deal.currency, locale);
  const isDrop = Number(deal.dropPercent) < 0;
  return {
    price: deal.price,
    currency: deal.currency,
    referencePrice: referencePrice === price ? null : deal.referencePrice,
    dropPercent: isDrop ? formatPriceDeltaPercent(deal.dropPercent, locale) : null,
  };
}

/** Artist, price snapshot, and match reason for a deal price-drop notification. */
export function DealNotificationDetails({
  notification,
}: {
  notification: ClientNotificationItem;
}) {
  const t = useTranslations("globals.notifications");
  const locale = useLocale();
  const { artistName } = notification.subject;
  const priceLine = dealPriceLine(notification.subject.deal, locale);

  return (
    <>
      {artistName ? (
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{artistName}</p>
      ) : null}
      {priceLine ? (
        <p className="mt-1 flex flex-wrap items-baseline gap-x-1.5 text-sm tabular-nums">
          <Price
            amount={priceLine.price}
            currency={priceLine.currency}
            compact
            className="font-semibold text-foreground"
          />
          {priceLine.referencePrice ? (
            <s className="text-xs text-muted-foreground">
              <Price
                amount={priceLine.referencePrice}
                currency={priceLine.currency}
                compact
                plain
              />
            </s>
          ) : null}
          {priceLine.dropPercent ? (
            <span className="text-xs font-bold text-emerald-400">−{priceLine.dropPercent}</span>
          ) : null}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground mt-0.5">
        {t("dealReason", { source: notification.matchSource ?? "other" })} ·{" "}
        <RelativeTime date={notification.createdAt} />
      </p>
    </>
  );
}
